import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { PurchaseOrder, PurchaseOrderStatus, HandlingUnit, HandlingUnitStage, PurchaseOrderHistoryEntry, UpdatePurchaseOrder } from './types/purchaseOrder';
import { WorkOrder, WorkOrderStatus } from './types/workOrder';
import { Client } from './types/client';
import { Product } from './types/product';
import { User } from './types/user';
import { Ticket, CreateTicketDto, UpdateTicketDto, CreateTicketNoteDto, TicketNote, TicketStatus } from './types/ticket';
import config from './config';
import { formatDate } from './utils';

// Interfaces
export interface RevenueDataPoint {
  date: string;
  revenue: number;
}

export interface ClientRevenue {
  clientName: string;
  date: string;
  revenue: number;
}

export interface ProductRevenue {
  productName: string;
  date: string;
  revenue: number;
}

export interface MonthlyGrowthRate {
  month: string;
  growthRate: number;
}

export interface AgingReport {
  fourPlusDays: PurchaseOrder[];
  tenPlusDays: PurchaseOrder[];
}

export interface CreatePurchaseOrderData {
  poNumber: string;
  clientId: number;
  items: {
    itemNumber: string;
    material: string;
    quantity: string;
    unitPrice: string;
    unit: string;
    currency: string;
    shipToParty: string;
    batchNumber: string;
    deliveryDate: string;
    hu: string[];
  }[];
  notes?: string;
  internalNotes?: string;
}

export interface TruckHandlingUnit extends HandlingUnit {
  poNumber?: string;
  itemNumber?: string;
  productName?: string;
  shipToParty?: string;
  truckStatus?: 'OPEN' | 'FULL';
}

// API Configuration
const API_BASE_URL = config.API_BASE_URL;
// Relative by default so the browser calls the SAME origin as the app; nginx
// proxies /pdf -> the pdf-parser service. Avoids the hardcoded prod URL + CORS.
// Override with REACT_APP_PDF_SERVICE_URL at build time if ever needed.
const PDF_SERVICE_URL = process.env.REACT_APP_PDF_SERVICE_URL || '/pdf';
const TOKEN_STORAGE_KEY = config.TOKEN_STORAGE_KEY;
const REFRESH_TOKEN_KEY = config.REFRESH_TOKEN_KEY;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth header management
const updateAuthHeader = (token: string | null) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
if (storedToken) {
  updateAuthHeader(storedToken);
}

// Interceptors
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      try {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
        const { accessToken, refreshToken: newRefreshToken } = response.data;
        localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
        updateAuthHeader(accessToken);

        const originalRequest = error.config;
        if (originalRequest && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Add new function for handling units
export const getAllHandlingUnits = async (): Promise<HandlingUnit[]> => {
  console.log('Fetching all handling units');
  try {
    const response = await api.get<HandlingUnit[]>('/handling-units');
    console.log('Handling units fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching handling units:', error);
    throw error;
  }
};

// Ticket functions
export const createTicket = async (ticket: CreateTicketDto): Promise<Ticket> => {
  console.log('Creating ticket:', ticket);
  try {
    const response = await api.post<Ticket>('/tickets', ticket);
    console.log('Ticket created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw error;
  }
};

export const uploadTicketAttachment = async (ticketId: number, file: File): Promise<Ticket> => {
  console.log(`Uploading attachment for ticket ${ticketId}`);
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<Ticket>(
      `/tickets/${ticketId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    console.log('File uploaded successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error uploading file to ticket ${ticketId}:`, error);
    throw error;
  }
};

export const removeTicketAttachment = async (ticketId: number, filename: string): Promise<Ticket> => {
  console.log(`Removing attachment ${filename} from ticket ${ticketId}`);
  try {
    const response = await api.delete<Ticket>(`/tickets/${ticketId}/attachments/${filename}`);
    console.log('File removed successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error removing file ${filename} from ticket ${ticketId}:`, error);
    throw error;
  }
};

export const downloadTicketAttachment = async (ticketId: number, filename: string): Promise<void> => {
  console.log(`Downloading attachment ${filename} from ticket ${ticketId}`);
  try {
    const response = await api.get(`/tickets/${ticketId}/attachments/${filename}`, {
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('File downloaded successfully');
  } catch (error) {
    console.error(`Error downloading file ${filename} from ticket ${ticketId}:`, error);
    throw error;
  }
};

export const getAllTickets = async (params?: { status?: TicketStatus; search?: string }): Promise<Ticket[]> => {
  console.log('Fetching all tickets with params:', params);
  try {
    const response = await api.get<Ticket[]>('/tickets', { params });
    console.log('Tickets fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching tickets:', error);
    throw error;
  }
};

export const getMyTickets = async (): Promise<Ticket[]> => {
  console.log('Fetching my tickets');
  try {
    const response = await api.get<Ticket[]>('/tickets/my-tickets');
    console.log('My tickets fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching my tickets:', error);
    throw error;
  }
};

export const getTicket = async (id: number): Promise<Ticket> => {
  console.log(`Fetching ticket with id: ${id}`);
  try {
    const response = await api.get<Ticket>(`/tickets/${id}`);
    console.log('Ticket fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ticket with id ${id}:`, error);
    throw error;
  }
};

export const updateTicket = async (id: number, ticket: UpdateTicketDto): Promise<Ticket> => {
  console.log(`Updating ticket with id ${id}:`, ticket);
  try {
    const response = await api.patch<Ticket>(`/tickets/${id}`, ticket);
    console.log('Ticket updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating ticket with id ${id}:`, error);
    throw error;
  }
};

export const deleteTicket = async (id: number): Promise<void> => {
  console.log(`Deleting ticket with id: ${id}`);
  try {
    await api.delete(`/tickets/${id}`);
    console.log(`Ticket with id ${id} deleted successfully`);
  } catch (error) {
    console.error(`Error deleting ticket with id ${id}:`, error);
    throw error;
  }
};

// Ticket Note functions
export const addNote = async (ticketId: number, note: CreateTicketNoteDto): Promise<TicketNote> => {
  console.log(`Adding note to ticket ${ticketId}:`, note);
  try {
    const response = await api.post<TicketNote>(`/tickets/${ticketId}/notes`, note);
    console.log('Note added successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error adding note to ticket ${ticketId}:`, error);
    throw error;
  }
};

export const getNotes = async (ticketId: number): Promise<TicketNote[]> => {
  console.log(`Fetching notes for ticket ${ticketId}`);
  try {
    const response = await api.get<TicketNote[]>(`/tickets/${ticketId}/notes`);
    console.log('Notes fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching notes for ticket ${ticketId}:`, error);
    throw error;
  }
};

export const updateTicketStatus = async (id: number, status: TicketStatus): Promise<Ticket> => {
  console.log(`Updating ticket ${id} status to ${status}`);
  try {
    const response = await api.patch<Ticket>(`/tickets/${id}/status`, { status });
    console.log('Ticket status updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating ticket ${id} status:`, error);
    throw error;
  }
};

// Purchase Order functions
export const getPurchaseOrders = async (params: {
  page: number;
  limit: number;
  search?: string;
  poNumber?: string;
  status?: PurchaseOrderStatus | '';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<[PurchaseOrder[], number]> => {
  console.log('Fetching purchase orders with params:', params);
  try {
    const response = await api.get<[PurchaseOrder[], number]>('/purchase-orders', { params });
    console.log('Purchase orders fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase orders:', error);
    throw error;
  }
};

export const getAgingReport = async (): Promise<AgingReport> => {
  console.log('Fetching aging report');
  try {
    const response = await api.get<AgingReport>('/purchase-orders/aging-report');
    console.log('Aging report fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching aging report:', error);
    throw error;
  }
};

export const getPurchaseOrderById = async (id: string | number, timestamp?: number) => {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  console.log(`Fetching purchase order with id: ${numericId}${timestamp ? ` at timestamp ${timestamp}` : ''}`);
  try {
    const params = timestamp !== undefined ? { timestamp } : undefined;
    const response = await api.get<PurchaseOrder>(`/purchase-orders/${numericId}`, { params });
    console.log('Purchase order fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching purchase order with id ${numericId}:`, error);
    throw error;
  }
};

export const createPurchaseOrder = async (purchaseOrderData: CreatePurchaseOrderData) => {
  console.log('Creating purchase order with data:', JSON.stringify(purchaseOrderData, null, 2));
  try {
    const response = await api.post<PurchaseOrder>('/purchase-orders', purchaseOrderData);
    console.log('Purchase order created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating purchase order:', error);
    throw error;
  }
};

export const updatePurchaseOrder = async (id: string | number, purchaseOrder: UpdatePurchaseOrder | Partial<PurchaseOrder>) => {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  console.log(`Updating purchase order with id ${numericId}:`, purchaseOrder);
  try {
    const response = await api.patch<PurchaseOrder>(`/purchase-orders/${numericId}`, purchaseOrder);
    console.log('Purchase order updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating purchase order with id ${numericId}:`, error);
    throw error;
  }
};

export const clearReview = async (id: number) => {
  console.log(`Clearing review flag for purchase order ${id}`);
  try {
    const response = await api.patch<PurchaseOrder>(`/purchase-orders/${id}/clear-review`);
    console.log('Purchase order review flag cleared successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error clearing review flag for purchase order ${id}:`, error);
    throw error;
  }
};

export const updatePurchaseOrderStatus = async (id: number, status: PurchaseOrderStatus): Promise<PurchaseOrder> => {
  console.log(`Updating purchase order ${id} status to ${status}`);
  try {
    // First get the current purchase order to include all required fields
    const currentPO = await getPurchaseOrderById(id);
    
    // Create update payload with all required fields
    const updateData: UpdatePurchaseOrder = {
      poNumber: currentPO.poNumber,
      status: status,
      items: currentPO.items.map(item => ({
        id: item.id,
        itemNumber: item.itemNumber,
        material: '',
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        unit: item.unit,
        currency: item.currency,
        shipToParty: item.shipToParty,
        batchNumber: item.batchNumber,
        stage: item.stage,
        deliveryDate: item.deliveryDate,
        handlingUnits: item.handlingUnits.map(hu => ({
          id: hu.id,
          huNumber: hu.huNumber,
          stage: hu.stage,
          deliveryDate: hu.deliveryDate,
          location: hu.location,
          receivedCount: hu.receivedCount,
          purchaseOrderItemId: hu.purchaseOrderItemId
        })),
        hu: item.handlingUnits.map(hu => hu.huNumber)
      }))
    };

    const response = await api.patch<PurchaseOrder>(`/purchase-orders/${id}`, updateData);
    console.log('Purchase order status updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating purchase order ${id} status:`, error);
    throw error;
  }
};

export const deletePurchaseOrder = async (id: string | number) => {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  console.log(`Deleting purchase order with id: ${numericId}`);
  try {
    await api.delete(`/purchase-orders/${numericId}`);
    console.log(`Purchase order with id ${numericId} deleted successfully`);
  } catch (error) {
    console.error(`Error deleting purchase order with id ${numericId}:`, error);
    throw error;
  }
};

export const approvePurchaseOrder = async (id: string | number) => {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  console.log(`Approving purchase order with id: ${numericId}`);
  try {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${numericId}/approve`);
    console.log('Purchase order approved successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error approving purchase order with id ${numericId}:`, error);
    throw error;
  }
};

export const rejectPurchaseOrder = async (id: string | number) => {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  console.log(`Rejecting purchase order with id: ${numericId}`);
  try {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${numericId}/reject`);
    console.log('Purchase order rejected successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error rejecting purchase order with id ${numericId}:`, error);
    throw error;
  }
};

export const sendToQuickbooks = async (id: string | number) => {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  console.log(`Sending purchase order with id ${numericId} to QuickBooks`);
  try {
    const response = await api.post<{ message: string }>(`/purchase-orders/${numericId}/send-to-quickbooks`);
    console.log('Purchase order sent to QuickBooks successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error sending purchase order with id ${numericId} to QuickBooks:`, error);
    throw error;
  }
};

export const getQuickbooksStatus = async () => {
  console.log('Fetching QuickBooks connection status');
  try {
    const response = await api.get<{ connected: boolean; lastSync: string | null }>('/quickbooks/status');
    console.log('QuickBooks status fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching QuickBooks status:', error);
    throw error;
  }
};

export const addComment = async (id: string | number, comment: string) => {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
  console.log(`Adding comment to purchase order with id ${numericId}:`, comment);
  try {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${numericId}/comments`, { comment });
    console.log('Comment added successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error adding comment to purchase order with id ${numericId}:`, error);
    throw error;
  }
};

// Work Order functions
export const getWorkOrders = async (params?: {
  page?: number;
  limit?: number;
  status?: WorkOrderStatus | '';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}) => {
  console.log('Fetching work orders with params:', params);
  try {
    const response = await api.get<{ data: WorkOrder[]; total: number; page: number; lastPage: number }>('/work-orders', { params });
    console.log('Work orders fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching work orders:', error);
    throw error;
  }
};

export const getWorkOrderById = async (id: number) => {
  console.log(`Fetching work order with id: ${id}`);
  try {
    const response = await api.get<WorkOrder>(`/work-orders/${id}`);
    console.log('Work order fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching work order with id ${id}:`, error);
    throw error;
  }
};

export const updateWorkOrder = async (id: number, workOrder: Partial<WorkOrder>) => {
  console.log(`Updating work order with id ${id}:`, workOrder);
  try {
    const response = await api.patch<WorkOrder>(`/work-orders/${id}`, workOrder);
    console.log('Work order updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating work order with id ${id}:`, error);
    throw error;
  }
};

// Client functions
export const getClients = async () => {
  console.log('Fetching clients');
  try {
    const response = await api.get<Client[]>('/clients');
    console.log('Clients fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
};

export const createClient = async (client: Partial<Client>) => {
  console.log('Creating client:', client);
  try {
    const response = await api.post<Client>('/clients', client);
    console.log('Client created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating client:', error);
    throw error;
  }
};

export const updateClient = async (id: number, client: Partial<Client>) => {
  console.log(`Updating client with id ${id}:`, client);
  try {
    const response = await api.patch<Client>(`/clients/${id}`, client);
    console.log('Client updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating client with id ${id}:`, error);
    throw error;
  }
};

export const deleteClient = async (id: number) => {
  console.log(`Deleting client with id: ${id}`);
  try {
    await api.delete(`/clients/${id}`);
    console.log(`Client with id ${id} deleted successfully`);
  } catch (error) {
    console.error(`Error deleting client with id ${id}:`, error);
    throw error;
  }
};

// Product functions
export const getProducts = async (params?: {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<{ data: Product[]; totalCount: number }> => {
  console.log('Fetching products with params:', params);
  try {
    const response = await api.get<{ data: Product[]; totalCount: number }>('/products', { params });
    console.log('Products fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

export const createProduct = async (product: Partial<Product>) => {
  console.log('Creating product:', product);
  try {
    const response = await api.post<Product>('/products', product);
    console.log('Product created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

export const updateProduct = async (id: number, product: Partial<Product>) => {
  console.log(`Updating product with id ${id}:`, product);
  try {
    const response = await api.patch<Product>(`/products/${id}`, product);
    console.log('Product updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating product with id ${id}:`, error);
    throw error;
  }
};

export const deleteProduct = async (id: number) => {
  console.log(`Deleting product with id: ${id}`);
  try {
    await api.delete(`/products/${id}`);
    console.log(`Product with id ${id} deleted successfully`);
  } catch (error) {
    console.error(`Error deleting product with id ${id}:`, error);
    throw error;
  }
};

// User functions
export const getUsers = async () => {
  console.log('Fetching users');
  try {
    const response = await api.get<User[]>('/users');
    console.log('Users fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const createUser = async (user: Partial<User>) => {
  console.log('Creating user:', user);
  try {
    const response = await api.post<User>('/users', user);
    console.log('User created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (id: number, user: Partial<User>) => {
  console.log(`Updating user with id ${id}:`, user);
  try {
    const response = await api.patch<User>(`/users/${id}`, user);
    console.log('User updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating user with id ${id}:`, error);
    throw error;
  }
};

export const deleteUser = async (id: number) => {
  console.log(`Deleting user with id: ${id}`);
  try {
    await api.delete(`/users/${id}`);
    console.log(`User with id ${id} deleted successfully`);
  } catch (error) {
    console.error(`Error deleting user with id ${id}:`, error);
    throw error;
  }
};

// Auth functions
export const login = async (username: string, password: string) => {
  console.log('Attempting login for user:', username);
  try {
    const response = await api.post<{ accessToken: string; refreshToken: string; user: User }>('/auth/login', { username, password });
    if (response.data.accessToken) {
      localStorage.setItem(TOKEN_STORAGE_KEY, response.data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
      updateAuthHeader(response.data.accessToken);
      console.log('Login successful');
    }
    return response.data;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

export const logout = async () => {
  console.log('Logging out');
  try {
    const response = await api.post('/auth/logout');
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    updateAuthHeader(null);
    console.log('Logout successful');
    return response.data;
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
};

// PDF Upload function
export const uploadPdf = async (files: File[], clientId: number, force: boolean = false) => {
  console.log('Uploading PDF files:', files.map(f => f.name), 'force:', force);
  try {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('file', file);
    });
    formData.append('clientId', clientId.toString());
    
    const response = await axios.post(`${PDF_SERVICE_URL}/process_pdf${force ? '?force=true' : ''}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('PDFs queued for processing:', response.data);
    
    if (response.data.jobs) {
      return {
        message: response.data.message,
        jobs: response.data.jobs,
        jobId: response.data.jobs[0].jobId,
        status: response.data.jobs[0].status
      };
    } else {
      return {
        message: response.data.message,
        jobs: [{ jobId: response.data.jobId, status: response.data.status }],
        jobId: response.data.jobId,
        status: response.data.status
      };
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      // Handle ConflictException
      const conflictData = error.response.data;
      throw {
        ...error,
        isConflict: true,
        existingId: conflictData.existingId,
        message: conflictData.message,
        canForce: conflictData.canForce
      };
    }
    console.error('Error uploading PDFs:', error);
    throw error;
  }
};

// PDF Download function
export const downloadPurchaseOrderPdf = async (id: number) => {
  console.log(`Downloading PDF for purchase order with id: ${id}`);
  try {
    const response = await api.get(`/pdf-parser/download/${id}`, {
      responseType: 'blob'
    });
    
    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `purchase-order-${id}.pdf`);
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL
    window.URL.revokeObjectURL(url);
    
    console.log('PDF downloaded successfully');
  } catch (error) {
    console.error(`Error downloading PDF for purchase order with id ${id}:`, error);
    throw error;
  }
};

// Revenue functions
export const getRevenueData = async (
  startDate: Date,
  endDate: Date,
  period: 'day' | 'week' | 'month' | 'year'
): Promise<RevenueDataPoint[]> => {
  console.log('Fetching revenue data:', { startDate, endDate, period });
  try {
    const response = await api.get<RevenueDataPoint[]>('/purchase-orders/revenue', {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        period
      }
    });
    console.log('Revenue data fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching revenue data:', error);
    throw error;
  }
};

export const getRpRevenueData = async (
  startDate: Date,
  endDate: Date,
  period: 'day' | 'week' | 'month' | 'year'
): Promise<RevenueDataPoint[]> => {
  console.log('Fetching RP revenue data:', { 
    startDate: startDate.toISOString(), 
    endDate: endDate.toISOString(), 
    period 
  });
  try {
    const response = await api.get<RevenueDataPoint[]>('/rp-revenue/over-time', {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        period: period
      }
    });
    console.log('RP Revenue data fetched successfully:', response.data);
    
    // Ensure proper number formatting for revenue values
    const formattedData = response.data.map(point => ({
      date: point.date,
      revenue: typeof point.revenue === 'string' ? parseFloat(point.revenue) : point.revenue
    }));
    
    console.log('Formatted revenue data:', formattedData);
    return formattedData;
  } catch (error) {
    console.error('Error fetching RP revenue data:', error);
    throw error;
  }
};

export const getRpRevenuePipeline = async (
  startDate: Date,
  endDate: Date,
  period: 'day' | 'week' | 'month' | 'year'
): Promise<RevenueDataPoint[]> => {
  console.log('Fetching RP pipeline revenue data:', { 
    startDate: startDate.toISOString(), 
    endDate: endDate.toISOString(), 
    period 
  });
  try {
    const response = await api.get<RevenueDataPoint[]>('/rp-revenue/pipeline', {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        period
      }
    });
    console.log('RP Pipeline revenue data fetched successfully:', response.data);
    
    // Ensure proper number formatting for revenue values
    const formattedData = response.data.map(point => ({
      date: point.date,
      revenue: typeof point.revenue === 'string' ? parseFloat(point.revenue) : point.revenue
    }));
    
    console.log('Formatted pipeline revenue data:', formattedData);
    return formattedData;
  } catch (error) {
    console.error('Error fetching RP pipeline revenue data:', error);
    throw error;
  }
};

export const getRpRevenueByClient = async (
  startDate: Date,
  endDate: Date,
  period: 'day' | 'week' | 'month' | 'year'
): Promise<ClientRevenue[]> => {
  console.log('Fetching RP revenue by client:', { 
    startDate: startDate.toISOString(), 
    endDate: endDate.toISOString(),
    period
  });
  try {
    const response = await api.get<ClientRevenue[]>('/rp-revenue/by-client', {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        period
      }
    });
    console.log('RP Revenue by client fetched successfully:', response.data);
    
    // Ensure proper number formatting for revenue values
    const formattedData = response.data.map(item => ({
      clientName: item.clientName,
      date: item.date,
      revenue: typeof item.revenue === 'string' ? parseFloat(item.revenue) : item.revenue
    }));
    
    console.log('Formatted client revenue data:', formattedData);
    return formattedData;
  } catch (error) {
    console.error('Error fefetching RP revenue by client:', error);
    throw error;
  }
};

export const getRpRevenueByProduct = async (
  startDate: Date,
  endDate: Date,
  period: 'day' | 'week' | 'month' | 'year'
): Promise<ProductRevenue[]> => {
  console.log('Fetching RP revenue by product:', { 
    startDate: startDate.toISOString(), 
    endDate: endDate.toISOString(),
    period
  });
  try {
    const response = await api.get<ProductRevenue[]>('/rp-revenue/by-product', {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        period
      }
    });
    console.log('RP Revenue by product fetched successfully:', response.data);    
    // Ensure proper number formatting for revenue values
    const formattedData = response.data.map(item => ({
      productName: item.productName,
      date: item.date,
      revenue: typeof item.revenue === 'string' ? parseFloat(item.revenue) : item.revenue
    }));
    
    console.log('Formatted product revenue data:', formattedData);
    return formattedData;
  } catch (error) {
    console.error('Error fetching RP revenue by product:', error);
    throw error;
  }
};

// Item Stage function
export const updateItemStage = async (itemId: number, stage: HandlingUnitStage, username: string): Promise<PurchaseOrder> => {
  console.log(`Updating item stage for item with id ${itemId} to ${stage}`);
  try {
    const historyEntry: PurchaseOrderHistoryEntry = {
      timestamp: new Date().toISOString(),
      action: 'Stage Updated',
      user: username,
      details: `Item ${itemId} stage updated to ${stage}`
    };

    const response = await api.patch<PurchaseOrder>(`/purchase-orders/items/${itemId}/stage`, { stage, historyEntry });
    console.log('Item stage updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating item stage for item with id ${itemId}:`, error);
    throw error;
  }
};

// Handling Unit functions
export const updateHandlingUnitStage = async (huNumber: string, stage: HandlingUnitStage, username: string): Promise<HandlingUnit> => {
  console.log(`Updating handling unit ${huNumber} stage to ${stage}`);
  try {
    const historyEntry: PurchaseOrderHistoryEntry = {
      timestamp: new Date().toISOString(),
      action: 'Stage Updated',
      user: username,
      details: `Handling unit stage updated to ${stage}`
    };

    const response = await api.patch<HandlingUnit>(`/handling-units/${huNumber}/stage`, {
      stage,
      historyEntry
    });
    console.log('Handling unit stage updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating handling unit ${huNumber} stage:`, error);
    throw error;
  }
};

export const updateHandlingUnitTruckNumber = async (huNumber: string, truckNumber: number | null, username: string): Promise<HandlingUnit> => {
  console.log(`Updating handling unit ${huNumber} truck number to ${truckNumber}`);
  try {
    const historyEntry: PurchaseOrderHistoryEntry = {
      timestamp: new Date().toISOString(),
      action: 'Truck Number Updated',
      user: username,
      details: `Handling unit outbound truck number updated to ${truckNumber ?? 'None'}`
    };

    const response = await api.patch<HandlingUnit>(`/handling-units/${huNumber}/truck-number`, {
      handlingUnitOutboundTruckNumber: truckNumber,
      historyEntry
    });
    console.log('Handling unit truck number updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating handling unit ${huNumber} truck number:`, error);
    throw error;
  }
};

export const updateHandlingUnitTruckDateTime = async (huNumber: string, date: Date | null, username: string): Promise<HandlingUnit> => {
  console.log(`Updating handling unit ${huNumber} truck date/time to ${date?.toISOString() ?? 'None'}`);
  try {
    const historyEntry: PurchaseOrderHistoryEntry = {
      timestamp: new Date().toISOString(),
      action: 'Truck Date/Time Updated',
      user: username,
      details: `Handling unit truck date/time updated to ${date ? formatDate(date) : 'None'}`
    };

    const response = await api.patch<HandlingUnit>(`/handling-units/${huNumber}/truck-date-time`, {
      huTruckDateTime: date?.toISOString() ?? null,
      historyEntry
    });
    console.log('Handling unit truck date/time updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating handling unit ${huNumber} truck date/time:`, error);
    throw error;
  }
};

export const updateHandlingUnitLocation = async (huNumber: string, location: string, username: string): Promise<HandlingUnit> => {
  console.log(`Updating handling unit ${huNumber} location to ${location}`);
  try {
    const historyEntry: PurchaseOrderHistoryEntry = {
      timestamp: new Date().toISOString(),
      action: 'Location Updated',
      user: username,
      details: `Handling unit location updated to ${location}`
    };

    const response = await api.patch<HandlingUnit>(`/handling-units/${huNumber}/location`, {
      location,
      historyEntry
    });
    console.log('Handling unit location updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating handling unit ${huNumber} location:`, error);
    throw error;
  }
};

export const updateHandlingUnitReceivedCount = async (huNumber: string, receivedCount: number, username: string): Promise<HandlingUnit> => {
  console.log(`Updating handling unit ${huNumber} received count to ${receivedCount}`);
  try {
    const historyEntry: PurchaseOrderHistoryEntry = {
      timestamp: new Date().toISOString(),
      action: 'Received Count Updated',
      user: username,
      details: `Handling unit received count updated to ${receivedCount}`
    };

    const response = await api.patch<HandlingUnit>(`/handling-units/${huNumber}/received-count`, {
      receivedCount,
      historyEntry
    });
    console.log('Handling unit received count updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating handling unit ${huNumber} received count:`, error);
    throw error;
  }
};

// Update handling unit notes
export const updateHandlingUnitNotes = async (huNumber: string, notes: string): Promise<HandlingUnit> => {
  console.log(`Updating handling unit ${huNumber} notes`);
  try {
    const response = await api.patch<HandlingUnit>(`/purchase-orders/handling-units/${huNumber}/notes`, { notes });
    console.log('Handling unit notes updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating handling unit ${huNumber} notes:`, error);
    throw error;
  }
};

// Truck Status function
export const updateTruckStatus = async (truckNumber: number, date: string, status: string, username: string): Promise<void> => {
  console.log(`Updating truck ${truckNumber} status to ${status} for date ${date}`);
  try {
    const historyEntry: PurchaseOrderHistoryEntry = {
      timestamp: new Date().toISOString(),
      action: 'Truck Status Updated',
      user: username,
      details: `Truck status updated to ${status}`
    };

    await api.patch(`/handling-units/truck/${truckNumber}/${date}/status`, {
      status,
      historyEntry
    });
    console.log('Truck status updated successfully');
  } catch (error) {
    console.error(`Error updating truck ${truckNumber} status:`, error);
    throw error;
  }
};

export const getTruckStatus = async (date: string): Promise<Record<number, TruckHandlingUnit[]>> => {
  console.log('Fetching truck status for date:', date);
  try {
    const response = await api.get<Record<number, TruckHandlingUnit[]>>('/handling-units/truck-status', {
      params: { date }
    });
    console.log('Truck status fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching truck status:', error);
    throw error;
  }
};

// QuickBooks functions
export const getQuickbooksTokenStatus = async (): Promise<{ tokenExists: boolean; isValid: boolean; isExpired: boolean }> => {
    console.log('Fetching QuickBooks token status');
    try {
        const response = await api.get<{ tokenExists: boolean; isValid: boolean; isExpired: boolean }>('/quickbooks/token-status');
        console.log('QuickBooks token status fetched successfully:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error fetching QuickBooks token status:', error);
        throw error;
    }
};

export const refreshQuickbooksToken = async (): Promise<void> => {
    console.log('Refreshing QuickBooks token');
    try {
        await api.get('/quickbooks/refresh-token');
        console.log('QuickBooks token refreshed successfully');
    } catch (error) {
        console.error('Error refreshing QuickBooks token:', error);
        throw error;
    }
};

export const clearQuickbooksToken = async (): Promise<{ message: string }> => {
    console.log('Clearing QuickBooks token');
    try {
        const response = await api.delete<{ message: string }>('/quickbooks/clear-token');
        console.log('QuickBooks token cleared successfully:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error clearing QuickBooks token:', error);
        throw error;
    }
};

export const createQuickbooksToken = async (tokenData: { accessToken: string; refreshToken: string; expiresIn: number }): Promise<{ message: string; tokenId: number; expiresAt: string }> => {
    console.log('Creating QuickBooks token');
    try {
        const response = await api.post<{ message: string; tokenId: number; expiresAt: string }>('/quickbooks/create-token', tokenData);
        console.log('QuickBooks token created successfully:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error creating QuickBooks token:', error);
        throw error;
    }
};

export const initiateQuickbooksAuth = (): void => {
    console.log('Initiating QuickBooks OAuth flow');
    // This will redirect the user to the QuickBooks authorization page
    window.location.href = `${API_BASE_URL}/quickbooks/auth`;
};

export const syncProductsWithQuickbooks = async (): Promise<{ message: string }> => {
    console.log('Syncing products with QuickBooks');
    try {
        const response = await api.post<{ message: string }>('/products/sync-quickbooks');
        console.log('Products synced with QuickBooks successfully:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error syncing products with QuickBooks:', error);
        throw error;
    }
};

export const syncSingleProductWithQuickbooks = async (productId: number): Promise<{ success: boolean; message: string }> => {
    console.log(`Syncing product ${productId} with QuickBooks`);
    try {
        const response = await api.post<{ success: boolean; message: string }>(`/products/sync-quickbooks/${productId}`);
        console.log(`Product ${productId} synced with QuickBooks successfully:`, response.data);
        return response.data;
    } catch (error) {
        console.error(`Error syncing product ${productId} with QuickBooks:`, error);
        throw error;
    }
};

export default api;
